import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { PreviewCommands } from '..';
import type { BridgeDataPayloads, IframeMessage, SyncData } from './types';

export interface UseIframeOptions {
    iframeRef: React.RefObject<HTMLIFrameElement | null>;
    initialData: SyncData;
    onMessage: (message: IframeMessage) => void;
    debounceMs?: number;
}

export function useIframe({ iframeRef, initialData, onMessage, debounceMs = 75 }: UseIframeOptions) {
    const [isReady, setIsReady] = useState(false);

    const sendMessage = useCallback(
        <K extends PreviewCommands>(method: K, payload: BridgeDataPayloads[K]) => {
            if (!iframeRef.current?.contentWindow) return;
            const { contentWindow } = iframeRef.current;

            const sendMessageDebounced = debounce(
                (method: PreviewCommands, payload: BridgeDataPayloads[PreviewCommands]) => {
                    contentWindow?.postMessage({ type: method, payload }, '*');
                },
                debounceMs
            );
            sendMessageDebounced(method, payload);
        },
        [iframeRef, debounceMs]
    );

    useEffect(() => {
        const handler = (event: MessageEvent<IframeMessage>) => {
            const data = event.data;
            onMessage(data);

            if (data.type === PreviewCommands.Ready && initialData) {
                sendMessage(PreviewCommands.Initialize, initialData);
                setIsReady(true);
            }
        };

        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [initialData, sendMessage, onMessage]);

    return { isReady, sendMessage };
}
